import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE, openSignedDownload } from "@/lib/api";
import { logger } from "@/lib/logger";
import { isNative } from "@/native/platform";
import { captureNativePhoto } from "@/native/camera";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UploadCloud,
  Camera,
  FileText,
  Sparkles,
  ArrowLeft,
  Focus,
  Send,
  MessageSquare,
  Link2,
  Link2Off,
  Eye,
  Download,
  Activity,
  DollarSign,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import ProofOfLossDialog from "@/components/ProofOfLossDialog";
import InvoicesCard from "@/components/InvoicesCard";
import EmailPacketDialog from "@/components/EmailPacketDialog";
import { TimelineItem, formatShortDuration } from "@/components/claim/TimelineItem";
import { PhotoCard } from "@/components/claim/PhotoCard";
import { SnoozeCards } from "@/components/claim/SnoozeCards";

export default function ClaimDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [claim, setClaim] = useState(null);
  const [files, setFiles] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [notes, setNotes] = useState([]);
  const [carrierGuide, setCarrierGuide] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [postingNote, setPostingNote] = useState(false);
  const [polDialogOpen, setPolDialogOpen] = useState(false);
  const [emailPacketOpen, setEmailPacketOpen] = useState(false);
  const photoInputRef = useRef();

  const refresh = useCallback(async () => {
    const [c, fs, es, ns] = await Promise.all([
      api.get(`/claims/${id}`),
      api.get(`/claims/${id}/files`),
      api.get(`/claims/${id}/estimates`),
      api.get(`/claims/${id}/notes`),
    ]);
    setClaim(c.data);
    setFiles(fs.data);
    setEstimates(es.data);
    setNotes(ns.data);
    if (c.data?.carrier) {
      api
        .get(`/carriers/${c.data.carrier}/guide`)
        .then((r) => setCarrierGuide(r.data))
        .catch(() => {
          /* carrier guide is optional UI — silent fail keeps the page usable */
        });
    }
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Active tab — drives polling for the Messages tab
  const [activeTab, setActiveTab] = useState("photos");
  const [timeline, setTimeline] = useState([]);
  const [timelineFilter, setTimelineFilter] = useState("all");

  // Load timeline whenever the user opens the Timeline tab
  useEffect(() => {
    if (activeTab !== "timeline") return;
    api
      .get(`/claims/${id}/timeline`)
      .then((r) => setTimeline(r.data))
      .catch(() => {
        /* timeline endpoint is best-effort — the tab will retry on next open */
      });
  }, [activeTab, id, files.length, notes.length, estimates.length]);

  // Mark messages seen whenever the user is on the Messages tab
  useEffect(() => {
    if (activeTab !== "messages") return;
    api.post(`/claims/${id}/notes/seen`).catch(() => {
      /* read-receipt is non-critical; ignore network blips */
    });
  }, [activeTab, id, notes.length]);

  // Poll messages every 5s while the Messages tab is open
  useEffect(() => {
    if (activeTab !== "messages") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/claims/${id}/notes`);
        if (!cancelled) setNotes(r.data);
      } catch (err) {
        // Transient network error — next 5s tick will retry. Log status so ops
        // can spot a sustained outage in the browser console.
        logger.debug("[claim-poll] notes refetch failed:", err?.response?.status);
      }
    };
    const interval = setInterval(tick, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [activeTab, id]);

  const photos = files.filter((f) => f.category === "photo");
  const estimateDocs = files.filter((f) => f.category === "estimate");
  const policyDocs = files.filter((f) => f.category === "policy");
  const historyDocs = files.filter((f) => f.category === "history");

  const handlePhoto = async (file) => {
    if (!file) return;
    setAnalyzing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/claims/${id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo analyzed by AI");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDocument = async (file, category) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    try {
      await api.post(`/claims/${id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const updateStatus = async (status) => {
    let body = { status };
    if (status === "approved" && claim?.status !== "approved") {
      const raw = window.prompt(
        "Settlement amount approved (USD).\nLeave blank to skip auto-invoicing.\nClaimScope will create a 15% success-fee invoice and email a pay link.",
        "",
      );
      if (raw && !Number.isNaN(parseFloat(raw))) {
        const settlement = parseFloat(raw);
        if (settlement > 0) {
          body.settlement_amount = settlement;
          // Offer 24h snooze so the policyholder can celebrate before the fee email lands.
          if (
            window.confirm(
              "Snooze the auto-invoice for 24 hours?\n\n" +
              "OK = approve now, send fee email tomorrow.\n" +
              "Cancel = approve + send fee email immediately.",
            )
          ) {
            body.snooze_auto_invoice = true;
          }
        }
      }
    }
    try {
      const r = await api.patch(`/claims/${id}`, body);
      if (r.data.auto_invoice) {
        toast.success(
          `Approved. Auto-invoice $${r.data.auto_invoice.amount.toFixed(2)} created and emailed.`,
          { duration: 6000 },
        );
      } else if (r.data.auto_invoice_snoozed) {
        const due = r.data.auto_invoice_snoozed.amount_when_due;
        toast.success(
          `Approved. Auto-invoice $${due.toFixed(2)} snoozed for 24h.`,
          { duration: 6000 },
        );
      } else {
        toast.success("Status updated");
      }
      refresh();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const runSnoozedInvoice = async () => {
    if (
      !window.confirm(
        "Send the fee invoice now? This emails the pay link to the policyholder immediately.",
      )
    ) return;
    try {
      const r = await api.post(`/claims/${id}/auto-invoice/run`);
      toast.success(
        `Invoice $${r.data.auto_invoice.amount.toFixed(2)} created and emailed.`,
        { duration: 6000 },
      );
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run invoice");
    }
  };

  const submitEstimate = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    try {
      await api.post(`/claims/${id}/estimates`, {
        contractor_id: user.id,
        amount: parseFloat(form.get("amount")),
        notes: form.get("notes"),
      });
      toast.success("Estimate submitted");
      e.target.reset();
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed";
      if (err?.response?.status === 402) {
        toast.error(msg, {
          action: {
            label: "Renew Pro",
            onClick: () => { window.location.href = "/dashboard"; },
          },
          duration: 8000,
        });
      } else {
        toast.error(msg);
      }
    }
  };

  const postNote = async (e) => {
    e.preventDefault();
    if (!noteBody.trim()) return;
    setPostingNote(true);
    try {
      await api.post(`/claims/${id}/notes`, { body: noteBody });
      setNoteBody("");
      const ns = await api.get(`/claims/${id}/notes`);
      setNotes(ns.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post message");
    } finally {
      setPostingNote(false);
    }
  };

  // Share link + view tracking
  const [shareViews, setShareViews] = useState(null);
  const canSeeShareViews =
    user?.role === "admin" ||
    (user?.role === "policyholder" && claim && claim.user_id === user.id);

  const refreshShareViews = useCallback(async () => {
    if (!user) return;
    if (user.role === "contractor") return;
    try {
      const r = await api.get(`/claims/${id}/share-views`);
      setShareViews(r.data);
    } catch (err) {
      // Share-view tracking is supplementary UI — main claim view still works.
      // Log so ops can spot a sustained share-views API outage.
      logger.debug("[claim] share-views fetch failed:", err?.response?.status);
    }
  }, [id, user]);

  useEffect(() => {
    if (!claim || !user) return;
    if (user.role === "contractor") return;
    refreshShareViews();
  }, [claim, user, refreshShareViews]);

  const createShareLink = async () => {
    try {
      const r = await api.post(`/claims/${id}/share`);
      const url = `${API_BASE}/share/timeline/${r.data.token}.pdf`;
      await navigator.clipboard.writeText(url).catch(() => {
        // clipboard API can be blocked (HTTP origin, permissions); the toast
        // below still surfaces the URL via `description` so the user can copy manually.
      });
      toast.success("Share link copied — expires in 7 days", {
        description: url.slice(0, 60) + "…",
      });
      refreshShareViews();
    } catch (e) {
      if (e?.response?.status === 429) {
        toast.error("Rate limit — max 10 share links per 24 hours on this claim");
      } else {
        toast.error(e?.response?.data?.detail || "Could not create share link");
      }
    }
  };

  const revokeShareLink = async (shareId) => {
    if (!window.confirm("Revoke this share link? Anyone holding the URL will see a 'revoked' message.")) return;
    try {
      await api.delete(`/shares/${shareId}`);
      toast.success("Share link revoked");
      refreshShareViews();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  if (!claim) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12 text-gray-500">
        Loading claim…
      </div>
    );
  }

  const backLink =
    user?.role === "admin"
      ? "/admin"
      : user?.role === "contractor"
      ? "/contractor"
      : "/dashboard";

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="claim-detail-page">
      <Link
        to={backLink}
        className="inline-flex items-center text-sm text-gray-600 hover:text-[#1E3F33] mb-6"
        data-testid="back-link"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#C25934] font-medium mb-2">
            Claim · {claim.id.slice(0, 8)}
          </p>
          <h1 className="font-['Outfit'] text-4xl font-semibold tracking-tight capitalize">
            {claim.damage_type} damage
          </h1>
          <p className="text-gray-600 mt-2">
            {claim.address} · {claim.zip_code}
          </p>
        </div>
        {user?.role === "admin" && (
          <div className="flex flex-col gap-3">
            <div className="w-60">
              <Label className="text-xs uppercase tracking-widest text-gray-500">
                Update status
              </Label>
              <Select
                value={claim.status}
                onValueChange={updateStatus}
              >
                <SelectTrigger
                  className="mt-1"
                  data-testid="admin-status-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="in_review">In review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <button
              type="button"
              onClick={() => openSignedDownload("timeline_pdf", id, `/claims/${id}/timeline.pdf`)}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-full border border-[#1E3F33] text-[#1E3F33] bg-white hover:bg-[#1E3F33] hover:text-[#F9F8F6] px-5 py-2 transition-colors"
              data-testid="export-timeline-pdf"
            >
              <FileText className="w-4 h-4" />
              Export timeline PDF
            </button>
            <button
              type="button"
              onClick={() => setEmailPacketOpen(true)}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-full border border-[#C25934] text-[#C25934] bg-white hover:bg-[#C25934] hover:text-[#F9F8F6] px-5 py-2 transition-colors"
              data-testid="email-packet-button-admin"
            >
              <Mail className="w-4 h-4" />
              Email packet
            </button>
            <button
              type="button"
              onClick={() => openSignedDownload("proof_of_loss_pdf", id, `/claims/${id}/proof-of-loss.pdf`)}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-full border border-[#1E3F33] text-[#1E3F33] bg-white hover:bg-[#1E3F33] hover:text-[#F9F8F6] px-5 py-2 transition-colors"
              data-testid="export-proof-of-loss-pdf"
            >
              <FileText className="w-4 h-4" />
              Proof of Loss
            </button>
            <button
              type="button"
              onClick={() => openSignedDownload("claim_audit_csv", id, `/claims/${id}/audit.csv`)}
              className="inline-flex items-center justify-center gap-2 text-xs text-gray-600 hover:text-[#1E3F33] underline-offset-2 hover:underline"
              data-testid="export-claim-audit-csv"
            >
              <Download className="w-3 h-3" />
              Audit CSV
            </button>
          </div>
        )}
        {user?.role === "policyholder" && (
          <div className="flex flex-col gap-2 md:items-end">
            <button
              type="button"
              onClick={() => openSignedDownload("timeline_pdf", id, `/claims/${id}/timeline.pdf`)}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-full border border-[#1E3F33] text-[#1E3F33] bg-white hover:bg-[#1E3F33] hover:text-[#F9F8F6] px-5 py-2 transition-colors"
              data-testid="export-timeline-pdf"
            >
              <FileText className="w-4 h-4" />
              Download claim packet (PDF)
            </button>
            <button
              onClick={createShareLink}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-full bg-[#C25934] text-[#F9F8F6] hover:opacity-90 px-5 py-2 transition-colors"
              data-testid="share-pdf-button"
            >
              <Link2 className="w-4 h-4" />
              Copy share link for my adjuster
            </button>
            <button
              type="button"
              onClick={() => setEmailPacketOpen(true)}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-full border border-[#C25934] text-[#C25934] bg-white hover:bg-[#C25934] hover:text-[#F9F8F6] px-5 py-2 transition-colors"
              data-testid="email-packet-button"
            >
              <Mail className="w-4 h-4" />
              Email packet to adjuster
            </button>
            <button
              type="button"
              onClick={() => setPolDialogOpen(true)}
              className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-full border border-[#1E3F33] text-[#1E3F33] bg-white hover:bg-[#1E3F33] hover:text-[#F9F8F6] px-5 py-2 transition-colors"
              data-testid="export-proof-of-loss-pdf"
            >
              <FileText className="w-4 h-4" />
              Generate Proof of Loss (PDF)
            </button>
            <button
              type="button"
              onClick={() => openSignedDownload("claim_audit_csv", id, `/claims/${id}/audit.csv`)}
              className="inline-flex items-center justify-center gap-2 text-xs text-gray-600 hover:text-[#1E3F33] underline-offset-2 hover:underline"
              data-testid="export-claim-audit-csv"
            >
              <Download className="w-3 h-3" />
              Download audit log (CSV)
            </button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Card className="border-[#E5E7EB] lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-['Outfit']">Claim summary</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4 text-sm">
            <Field label="Policyholder" value={claim.full_name} />
            <Field label="Phone" value={claim.phone} />
            <Field label="Email" value={claim.email} />
            <Field label="Date of loss" value={claim.date_of_loss} />
            <Field label="Carrier" value={claim.carrier} />
            <Field label="Policy #" value={claim.policy_number} />
            <div className="md:col-span-2">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">
                Description
              </p>
              <p className="text-gray-800 leading-relaxed">
                {claim.description}
              </p>
            </div>
          </CardContent>
        </Card>

        {carrierGuide && (
          <Card className="border-[#E5E7EB] bg-[#1E3F33] text-[#F9F8F6]">
            <CardHeader>
              <CardTitle className="font-['Outfit'] text-[#F9F8F6] flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#C25934]" /> Carrier guide
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-[#F9F8F6]/85 leading-relaxed space-y-3">
              <p>
                <span className="text-[#C25934] font-medium">
                  {carrierGuide.name}
                </span>{" "}
                — Proof of Loss within {carrierGuide.proof_of_loss_days} days.
              </p>
              <p>{carrierGuide.guide}</p>
              <Link
                to={`/guide/${carrierGuide.id}`}
                className="inline-block mt-2 text-[#C25934] hover:underline"
                data-testid="open-carrier-guide"
              >
                Open full guide →
              </Link>
            </CardContent>
          </Card>
        )}
      </div>

      {canSeeShareViews && shareViews && shareViews.links_issued > 0 && (
        <Card className="border-[#E5E7EB] mb-8" data-testid="share-views-card">
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-[#C25934]" />
                <h3 className="font-['Outfit'] font-semibold text-lg">
                  Claim packet tracking
                </h3>
              </div>
              <div className="flex gap-3 text-sm">
                <span className="text-gray-600">
                  <span className="font-semibold text-[#1E3F33]">
                    {shareViews.links_issued}
                  </span>{" "}
                  link{shareViews.links_issued === 1 ? "" : "s"} issued
                </span>
                <span className="text-gray-600">
                  <span className="font-semibold text-[#1E3F33]">
                    {shareViews.total_views}
                  </span>{" "}
                  view{shareViews.total_views === 1 ? "" : "s"}
                </span>
              </div>
            </div>
            {shareViews.views.length === 0 ? (
              <p className="text-sm text-gray-500">
                Your adjuster has not opened the packet yet.
              </p>
            ) : (
              <div className="space-y-2">
                {shareViews.views.slice(0, 5).map((v) => (
                  <div
                    key={v.id}
                    className="flex items-center justify-between text-sm py-2 border-b border-[#E5E7EB] last:border-0"
                    data-testid={`share-view-${v.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Eye className="w-4 h-4 text-[#1E3F33] flex-shrink-0" />
                      <div className="min-w-0">
                        {v.emailed_to ? (
                          <p
                            className="text-gray-800 font-medium truncate"
                            data-testid={`share-view-emailed-to-${v.id}`}
                          >
                            {v.emailed_to}
                          </p>
                        ) : null}
                        <p className="text-xs text-gray-500 truncate">
                          {v.user_agent || "Unknown browser"}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 text-gray-500 text-xs flex-shrink-0 ml-3">
                      {typeof v.opened_after_seconds === "number" && (
                        <span
                          className="text-[#C25934] font-medium"
                          data-testid={`share-view-delta-${v.id}`}
                        >
                          opened {formatShortDuration(v.opened_after_seconds)} after send
                        </span>
                      )}
                      <span>{v.ip || "—"} · {new Date(v.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
                {shareViews.views.length > 5 && (
                  <p className="text-xs text-gray-500 pt-1">
                    +{shareViews.views.length - 5} earlier view
                    {shareViews.views.length - 5 === 1 ? "" : "s"}
                  </p>
                )}
              </div>
            )}
            {shareViews.last_revocation && (
              <div className="mt-5 pt-4 border-t border-[#E5E7EB]">
                <p className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                  <Link2Off className="w-3.5 h-3.5 text-[#C25934]" />
                  <span>Last revoked:</span>
                  <span className="text-gray-700 font-medium">
                    link {shareViews.last_revocation.jti?.slice(0, 8)}
                  </span>
                  <span>·</span>
                  <span className="text-gray-700">
                    {shareViews.last_revocation.revoked_by_name ||
                      (shareViews.last_revocation.revoked_by_role === "system"
                        ? "Auto-revoke"
                        : "Unknown")}
                  </span>
                  {shareViews.last_revocation.reason === "auto_unused_7d" && (
                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#F9F8F6] border border-[#E5E7EB] text-gray-600">
                      auto · unused 7d
                    </span>
                  )}
                  <span>·</span>
                  <span>
                    {new Date(shareViews.last_revocation.revoked_at).toLocaleString()}
                  </span>
                </p>
              </div>
            )}
            {shareViews.links?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-[#E5E7EB]">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">
                  Active links
                </p>
                <div className="space-y-1.5">
                  {shareViews.links.map((lk) => (
                    <div
                      key={lk.id}
                      className="flex items-center justify-between text-xs py-1"
                      data-testid={`share-link-${lk.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <Link2
                          className={`w-3.5 h-3.5 ${
                            lk.revoked ? "text-gray-400" : "text-[#1E3F33]"
                          }`}
                        />
                        <span
                          className={
                            lk.revoked
                              ? "text-gray-400 line-through"
                              : "text-gray-700"
                          }
                        >
                          {lk.id.slice(0, 8)} · expires{" "}
                          {new Date(lk.expires_at).toLocaleDateString()}
                        </span>
                        {lk.revoked && (
                          <span className="text-[10px] uppercase tracking-widest text-gray-400 ml-1">
                            revoked
                          </span>
                        )}
                      </div>
                      {!lk.revoked && (
                        <button
                          onClick={() => revokeShareLink(lk.id)}
                          className="text-[#C25934] hover:underline inline-flex items-center gap-1"
                          data-testid={`revoke-share-${lk.id}`}
                        >
                          <Link2Off className="w-3 h-3" />
                          Revoke
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {user?.role !== "contractor" && (
        <InvoicesCard claim={claim} userRole={user?.role} />
      )}

      <SnoozeCards
        claim={claim}
        user={user}
        onRunSnoozedInvoice={runSnoozedInvoice}
      />

      <Tabs
        defaultValue="photos"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList data-testid="claim-tabs">
          <TabsTrigger value="photos" data-testid="tab-photos">
            Photos · {photos.length}
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            Documents · {estimateDocs.length + policyDocs.length + historyDocs.length}
          </TabsTrigger>
          <TabsTrigger value="estimates" data-testid="tab-estimates">
            Estimates · {estimates.length}
          </TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">
            Messages · {notes.length}
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">
            <Activity className="w-3.5 h-3.5 mr-1" />
            Timeline
          </TabsTrigger>
        </TabsList>

        {/* PHOTOS TAB */}
        <TabsContent value="photos" className="mt-6">
          <Card className="border-[#E5E7EB] mb-6">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row gap-6 items-stretch">
                <div className="md:w-1/2 relative bg-[#0b1f17] rounded-xl aspect-[4/3] overflow-hidden flex items-center justify-center">
                  {/* AI overlay */}
                  <div className="absolute inset-6 border-2 border-[#C25934]/70 rounded-lg pointer-events-none">
                    <span className="absolute -top-px -left-px w-6 h-6 border-t-2 border-l-2 border-[#C25934]" />
                    <span className="absolute -top-px -right-px w-6 h-6 border-t-2 border-r-2 border-[#C25934]" />
                    <span className="absolute -bottom-px -left-px w-6 h-6 border-b-2 border-l-2 border-[#C25934]" />
                    <span className="absolute -bottom-px -right-px w-6 h-6 border-b-2 border-r-2 border-[#C25934]" />
                  </div>
                  <div className="scan-line" />
                  <div className="text-center text-white/70 px-6">
                    <Focus className="w-10 h-10 mx-auto mb-3 text-[#C25934]" />
                    <p className="text-sm tracking-wide">
                      Line up the damaged corner inside the frame, then capture.
                    </p>
                  </div>
                  <div className="absolute top-4 left-4 bg-black/55 backdrop-blur text-white text-xs px-3 py-1 rounded-full">
                    AI guidance · Live
                  </div>
                </div>

                <div className="md:w-1/2 flex flex-col">
                  <h3 className="font-['Outfit'] text-xl font-semibold mb-2">
                    AI-assisted photo capture
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Upload a photo from your camera. Our model tags moisture,
                    soot, shingle uplift, and more — automatically.
                  </p>

                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handlePhoto(e.target.files?.[0])}
                    data-testid="photo-input"
                  />
                  <Button
                    className="btn-primary rounded-full"
                    disabled={analyzing}
                    onClick={async () => {
                      if (isNative()) {
                        try {
                          const { blob, fileName } = await captureNativePhoto("camera");
                          // Wrap the native Blob in a File so the rest of the
                          // upload pipeline (which inspects `.name`) just works.
                          handlePhoto(new File([blob], fileName, { type: blob.type }));
                        } catch (err) {
                          // User cancelled or permission denied. Silent — native
                          // OS already showed the permission sheet.
                          logger.debug("[native-camera] capture aborted:", err);
                        }
                      } else {
                        photoInputRef.current?.click();
                      }
                    }}
                    data-testid="capture-photo-button"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {analyzing ? "Analyzing with AI…" : "Capture / upload photo"}
                  </Button>
                  <p className="text-xs text-gray-500 mt-3">
                    JPEG, PNG, WEBP — max 12MB. GPS + timestamp locked.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {photos.length === 0 && (
              <p className="text-gray-500 col-span-full text-center py-12">
                No photos yet. Capture damage to populate your file.
              </p>
            )}
            {photos.map((p) => (
              <PhotoCard key={p.id} photo={p} />
            ))}
          </div>
        </TabsContent>

        {/* DOCUMENTS TAB */}
        <TabsContent value="documents" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-5">
            <DropZone
              title="Contractor estimate"
              caption="Xactimate, Symbility, PDF"
              category="estimate"
              files={estimateDocs}
              onFile={(f) => handleDocument(f, "estimate")}
              uploading={uploading}
            />
            <DropZone
              title="Policy declarations"
              caption="Your dec page or full policy"
              category="policy"
              files={policyDocs}
              onFile={(f) => handleDocument(f, "policy")}
              uploading={uploading}
            />
            <DropZone
              title="Prior claim history"
              caption="Past claim documents"
              category="history"
              files={historyDocs}
              onFile={(f) => handleDocument(f, "history")}
              uploading={uploading}
            />
          </div>
        </TabsContent>

        {/* ESTIMATES TAB */}
        <TabsContent value="estimates" className="mt-6">
          {user?.role === "contractor" && (
            <Card className="border-[#E5E7EB] mb-6">
              <CardHeader>
                <CardTitle className="font-['Outfit']">
                  Submit your estimate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={submitEstimate}
                  className="grid md:grid-cols-3 gap-4 items-end"
                  data-testid="estimate-form"
                >
                  <div>
                    <Label htmlFor="amount">Amount (USD)</Label>
                    <Input
                      id="amount"
                      name="amount"
                      type="number"
                      step="0.01"
                      required
                      className="mt-1"
                      data-testid="estimate-amount-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="notes">Scope notes</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      rows={3}
                      className="mt-1"
                      data-testid="estimate-notes-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="btn-primary rounded-full md:col-span-3"
                    data-testid="estimate-submit-button"
                  >
                    Submit estimate
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          <Card className="border-[#E5E7EB]">
            <CardHeader>
              <CardTitle className="font-['Outfit']">All estimates</CardTitle>
            </CardHeader>
            <CardContent>
              {estimates.length === 0 ? (
                <p className="text-gray-500 py-8 text-center">
                  No estimates yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {estimates.map((e) => (
                    <div
                      key={e.id}
                      className="flex items-center justify-between py-4 border-b border-[#E5E7EB] last:border-0"
                    >
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {e.company_name || e.contractor_name}
                          {e.contractor_is_pro && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full border border-[#C25934] text-[#C25934] bg-[#FEF3EC] font-semibold"
                              data-testid={`pro-badge-${e.id}`}
                              title="ClaimScope Pro member (verified + active)"
                            >
                              <ShieldCheck className="w-3 h-3" />
                              Pro
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {e.notes}
                        </p>
                      </div>
                      <p className="font-['Outfit'] text-xl font-semibold text-[#1E3F33]">
                        ${Number(e.amount).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MESSAGES TAB */}
        <TabsContent value="messages" className="mt-6">
          <Card className="border-[#E5E7EB]">
            <CardHeader>
              <CardTitle className="font-['Outfit'] flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#C25934]" />
                Claim conversation
                <span className="ml-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-gray-500 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-[460px] overflow-y-auto pr-1 mb-6">
                {notes.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-12">
                    No messages yet — start the conversation with your adjuster
                    or contractor.
                  </p>
                ) : (
                  notes.map((n) => {
                    const isMine = n.author_id === user?.id;
                    return (
                      <div
                        key={n.id}
                        className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                        data-testid={`note-${n.id}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-2xl px-4 py-3 border ${
                            isMine
                              ? "bg-[#1E3F33] text-[#F9F8F6] border-[#1E3F33] rounded-br-sm"
                              : "bg-white border-[#E5E7EB] rounded-bl-sm"
                          }`}
                        >
                          <p
                            className={`text-[10px] uppercase tracking-widest mb-1 ${
                              isMine ? "text-[#F9F8F6]/60" : "text-[#C25934]"
                            }`}
                          >
                            {n.author_role} · {n.author_name}
                          </p>
                          <p className="text-sm whitespace-pre-wrap leading-relaxed">
                            {n.body}
                          </p>
                          <p
                            className={`text-[10px] mt-1 ${
                              isMine ? "text-[#F9F8F6]/55" : "text-gray-400"
                            }`}
                          >
                            {new Date(n.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <form
                onSubmit={postNote}
                className="flex items-end gap-3"
                data-testid="message-form"
              >
                <Textarea
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                  placeholder="Write a message — visible to the policyholder, adjuster, and contractor on this claim."
                  rows={3}
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  className="btn-primary rounded-full"
                  disabled={postingNote || !noteBody.trim()}
                  data-testid="message-send-button"
                >
                  <Send className="w-4 h-4 mr-1" />
                  Send
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TIMELINE TAB */}
        <TabsContent value="timeline" className="mt-6">
          <Card className="border-[#E5E7EB]">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <CardTitle className="font-['Outfit'] flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#C25934]" />
                  Activity timeline
                </CardTitle>
                <div
                  className="flex flex-wrap gap-1.5"
                  data-testid="timeline-filters"
                >
                  {TIMELINE_FILTERS.map((f) => {
                    const count =
                      f.id === "all"
                        ? timeline.length
                        : timeline.filter((e) => e.kind === f.id).length;
                    const active = timelineFilter === f.id;
                    return (
                      <button
                        key={f.id}
                        onClick={() => setTimelineFilter(f.id)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          active
                            ? "bg-[#1E3F33] text-[#F9F8F6] border-[#1E3F33]"
                            : "bg-white text-gray-700 border-[#E5E7EB] hover:border-[#1E3F33]"
                        }`}
                        data-testid={`timeline-filter-${f.id}`}
                      >
                        {f.label}
                        {count > 0 && (
                          <span
                            className={`ml-1.5 text-[10px] ${
                              active ? "text-[#F9F8F6]/75" : "text-gray-400"
                            }`}
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const filtered =
                  timelineFilter === "all"
                    ? timeline
                    : timeline.filter((e) => e.kind === timelineFilter);
                if (filtered.length === 0) {
                  return (
                    <p className="text-gray-500 text-sm text-center py-12">
                      No activity{timelineFilter !== "all" ? " for this filter" : ""} yet.
                    </p>
                  );
                }
                return (
                  <ol className="relative border-l border-[#E5E7EB] ml-3 space-y-6 pl-8">
                    {filtered.map((ev, idx) => (
                      <TimelineItem
                        key={`${ev.kind}-${ev.created_at}-${idx}`}
                        event={ev}
                      />
                    ))}
                  </ol>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <ProofOfLossDialog
        open={polDialogOpen}
        onOpenChange={setPolDialogOpen}
        claim={claim}
      />
      <EmailPacketDialog
        open={emailPacketOpen}
        onOpenChange={setEmailPacketOpen}
        claim={claim}
      />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-gray-500 mb-0.5">
        {label}
      </p>
      <p className="text-gray-800">{value || "—"}</p>
    </div>
  );
}

const TIMELINE_FILTERS = [
  { id: "all", label: "All" },
  { id: "message", label: "Messages" },
  { id: "photo", label: "Photos" },
  { id: "document", label: "Documents" },
  { id: "estimate", label: "Estimates" },
  { id: "status_change", label: "Status" },
  { id: "share_open", label: "Opens" },
  { id: "email", label: "Emails" },
];

function DropZone({ title, caption, category, files, onFile, uploading }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  return (
    <Card className="border-[#E5E7EB]">
      <CardHeader>
        <CardTitle className="font-['Outfit'] text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files?.[0];
            if (f) onFile(f);
          }}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
            drag
              ? "border-[#1E3F33] bg-[#F9F8F6]"
              : "border-[#E5E7EB] bg-[#F9F8F6]/40 hover:border-[#1E3F33]"
          }`}
          data-testid={`dropzone-${category}`}
        >
          <UploadCloud className="w-8 h-8 mx-auto mb-2 text-[#1E3F33]" />
          <p className="text-sm text-gray-700">
            {uploading ? "Uploading…" : "Drop file or click to upload"}
          </p>
          <p className="text-xs text-gray-500 mt-1">{caption}</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".pdf,image/jpeg,image/png,image/webp,.doc,.docx"
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          data-testid={`file-input-${category}`}
        />

        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            {files.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => openSignedDownload("file_download", f.id, `/files/${f.id}/download`)}
                className="w-full text-left flex items-center gap-2 text-sm text-gray-800 hover:text-[#1E3F33] py-2 border-b border-[#E5E7EB] last:border-0"
                data-testid={`file-link-${f.id}`}
              >
                <FileText className="w-4 h-4 text-[#1E3F33]" />
                <span className="flex-1 truncate">{f.original_filename}</span>
                <span className="text-xs text-gray-500">
                  {(f.size / 1024).toFixed(0)} KB
                </span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
